import { useCallback } from "react";
import { toast } from "@jsq/ds/core";
import { addError } from "@jsq/observability";
import { formatError } from "#common/hooks/useReportError";
import {
  useApproveCanopyPaymentBatchMutation,
  useApproveGpxPaymentBatchMutation,
} from "#graphql/supergraphHooks";
import { clientContext } from "#utils/graphql_utils";
import { usePaymentsContext } from "../../Context/PaymentsContext";
import { triggerPaymentBatchesRefresh } from "../../PaymentBatchesTable/refreshSignal";
import type { UsePaymentBatchApproveParams, UsePaymentBatchApproveResult } from "./types";

export function usePaymentBatchApprove({
  paymentBatchId,
  isFinal,
  updatedProcessDate,
}: UsePaymentBatchApproveParams): UsePaymentBatchApproveResult {
  const { isGPX } = usePaymentsContext();

  const handleCanopyError = useCallback((err: Error) => {
    addError(formatError(err));
    toast.error(err.message);
  }, []);

  const [approveCanopyPaymentBatch, { loading: canopyLoading }] =
    useApproveCanopyPaymentBatchMutation({
      context: clientContext(),
      variables: { paymentBatchId },
      onCompleted: triggerPaymentBatchesRefresh,
      onError: handleCanopyError,
    });

  const [approveGPXPaymentBatch, { loading: gpxLoading }] = useApproveGpxPaymentBatchMutation({
    context: clientContext(),
    variables: {
      approvePaymentBatchInput: {
        paymentBatchId,
        isFinal,
        updatedProcessDate,
      },
    },
    onCompleted: triggerPaymentBatchesRefresh,
    onError: (err) => {
      addError(formatError(err));
      toast.error(err.message);
    },
  });

  const handleApprove = useCallback(() => {
    if (isGPX) {
      void approveGPXPaymentBatch();
    } else {
      void approveCanopyPaymentBatch();
    }
  }, [isGPX, approveGPXPaymentBatch, approveCanopyPaymentBatch]);

  const loading = isGPX ? gpxLoading : canopyLoading;

  return { handleApprove, loading };
}
